import { Bell, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { usePermissions } from '@/features/auth';
import { useMarketTimeZone, useSelectedCountry } from '@/features/country-scope';
import { lastNDays } from '@/shared/components/date-range-picker';
import { formatNumber } from '@/shared/lib/format';

import { useDashboard } from '../api/dashboard.queries';
import { QUEUES } from '../lib/attention-queues';

/**
 * Workspace alerts: the dashboard's "needs attention" queues, on every page,
 * for the selected country. Only queues the staff member may see are counted
 * (the backend returns null for the others) and only non-empty ones are
 * listed. Needs dashboard.read; renders nothing without it.
 */
export function AlertsMenu() {
  const { can } = usePermissions();
  const canSee = can('dashboard.read');
  if (!canSee) return null;
  return <AlertsMenuInner />;
}

function AlertsMenuInner() {
  const { country } = useSelectedCountry();
  const zone = useMarketTimeZone();
  const [open, setOpen] = useState(false);
  const range = useMemo(() => lastNDays(30, zone), [zone]);
  const dashboard = useDashboard({ from: range.from, to: range.to, countryId: country?.id });

  const attention = dashboard.data?.attention;
  const active = QUEUES.map((queue) => ({
    ...queue,
    count: attention?.[queue.key] ?? null,
  })).filter(
    (queue): queue is typeof queue & { count: number } => queue.count !== null && queue.count > 0,
  );
  const total = active.reduce((sum, queue) => sum + queue.count, 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-8"
          aria-label={
            total > 0 ? `Alerts: ${total} items need attention` : 'Alerts: nothing needs attention'
          }
        >
          <Bell className="size-4" aria-hidden />
          {total > 0 && (
            <span className="bg-primary text-primary-foreground absolute -end-0.5 -top-0.5 min-w-4 rounded-full px-1 text-[10px] leading-4 font-semibold tabular-nums">
              {total > 99 ? '99+' : total}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2">
        <p className="px-1 pb-1 text-xs font-semibold">Needs attention</p>
        {dashboard.isLoading ? (
          <p className="text-muted-foreground px-1 py-2 text-xs">Loading…</p>
        ) : dashboard.error ? (
          <p className="text-muted-foreground px-1 py-2 text-xs">Alerts could not be loaded.</p>
        ) : active.length === 0 ? (
          <p className="text-muted-foreground px-1 py-2 text-xs">Nothing needs attention.</p>
        ) : (
          <ul className="divide-y" aria-label="Alerts">
            {active.map((queue) => (
              <li key={queue.key}>
                <Link
                  to={queue.to}
                  onClick={() => setOpen(false)}
                  className="hover:bg-muted/50 flex items-center justify-between gap-3 rounded-sm px-1 py-2 text-xs"
                >
                  <span>{queue.label}</span>
                  <span className="flex items-center gap-1 font-semibold tabular-nums">
                    {formatNumber(queue.count)}
                    <ChevronRight
                      className="text-muted-foreground size-3.5 rtl:rotate-180"
                      aria-hidden
                    />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
